import {
  Offer,
  OfferProduct,
  Product,
  ProductPriceUnit,
  ProductType,
} from 'queries';

export const areSameProducts = (p1: Product, p2: Product) =>
  p1.name === p2.name || (p1.feature && p1.feature === p2.feature);

// Get an arbitrary value to be able to sort product
const getOrderValue = (product: Product, bonus?: number) => {
  const orderedTypes: ProductType[] = [
    ProductType.PARTICIPANT_FEE,
    ProductType.INCENTIVE,
    ProductType.CREDITS,
    ProductType.SUBSCRIPTION,
    ProductType.FEATURE,
  ];

  const orderedPrices: ProductPriceUnit[] = [
    ProductPriceUnit.EUR,
    ProductPriceUnit.EUR_FOR_ONE_HOUR_DURATION,
    ProductPriceUnit.INCENTIVE_PERCENT,
  ];
  const typeScore = orderedTypes.findIndex((type) => product.type == type);
  const priceScore = orderedPrices.findIndex(
    (unit) => unit == product.priceUnit,
  );

  return (bonus || 0) * 100 + typeScore * 10 + priceScore;
};

export type OfferProductOrdersInfos = Record<string, number>;

export const compareProducts = (
  a: Product,
  b: Product,
  orderInfos?: OfferProductOrdersInfos,
) => {
  const aScore = orderInfos
    ? getOrderValue(a, orderInfos[a.id])
    : getOrderValue(a);
  const bScore = orderInfos
    ? getOrderValue(b, orderInfos[b.id])
    : getOrderValue(b);

  if (aScore - bScore !== 0) {
    return aScore - bScore;
  }
  return a.name.localeCompare(b.name);
};

/**
 * We set a lower score to the most present products among the offer
 * We also check for similar type to handle case where
 *  Offer 1 : 5000 Credits
 *  Offer 2 : 10000 Credits
 */
export const getOfferProductOrdersInfos = (_offers: Array<Partial<Offer>>) => {
  const offers = _offers.filter((o) => !!(o.products && o.id)) as Array<{
    id: string;
    products: OfferProduct[];
  }>;

  const offerProducts = offers.reduce((acc, offer) => {
    acc[offer.id] = offer.products
      .filter((p) => !p.forRecruitmentByClient)
      .map(({ product }) => product!);
    return acc;
  }, {} as Record<string, Product[]>);

  const productScore: OfferProductOrdersInfos = {};
  for (const offer of offers) {
    const products = offerProducts[offer.id];

    for (const product of products) {
      let sameCount = offers.filter((o) => {
        return offerProducts[o.id].find((p) => p.name === product.name);
      }).length;

      if (
        sameCount === 1 &&
        (product.type !== ProductType.FEATURE || product.feature)
      ) {
        const sameTypes = offers
          .map((o) =>
            o.products.filter(
              ({ product: p }) =>
                p &&
                p.type === product.type &&
                p.category === product.category &&
                p.feature === product.feature &&
                p.importantFeature === product.importantFeature,
            ),
          )
          .flat();

        sameCount = sameTypes.length;
      }
      productScore[product.id] = offers.length - sameCount;
    }
  }
  return productScore;
};
